import { useState, useRef, useEffect, useMemo, useCallback } from 'preact/hooks';
import getProductType from '../../utils/getProductType';
import 'react-day-picker/style.css';
import dayjs from 'dayjs';
import { Text, withText } from 'preact-i18n';
import Modal from '../Modal';
import { selectIcon } from '../../templates/place';
import mixpanelTracker from '../../utils/mixpanelTracker';
import differenceInDays from 'date-fns/differenceInDays';
import useCalendarAvailability from './hooks/useCalendarAvailability';
import useAvailabilityWindow from './hooks/useAvailabilityWindow';
import {
  atDayStart,
  groupDatesByPriceLevel,
  makeUnavailableMatcher,
  shouldResetRangeSelection,
  toDate,
  toDateString,
} from './utils';
import { CALENDAR_CONTINUE_EVENT, CALENDAR_EVENT, TAB_TO_SECTION } from './constants';
import CalendarSection from './components/CalendarSection';
import DateTriggerRow from './components/DateTriggerRow';
import { HiddenNav, MonthCaption } from './components/MonthCaption';
import useDebounce from './hooks/useDebounce';
import OpenTicketSection from './components/OpenTicketSection';
import PriceLevelLegend from './components/PriceLevelLegend';

/**
 * Unified date picker that replaces the two separate Datepicker components when the
 * `unifiedDatePicker` feature is active.
 */
const UnifiedDatePicker = ({
  departureDate,
  returnDate,
  returnPlaceholder,
  tripType,
  dateFormat,
  maxDaysSearch,
  openTicketText,
  showOpenTicket,
  flatVariant,
  error,
  showMundialButton,
  showTomorrowWithMundial,
  locale,
  onTripTypeChange,
  onDepartureChange,
  onReturnChange,
  onReturnOpenTicket,
  onDepartureOpenTicket,
  onFocus,
  onContinue,
  departureBadge,
  openTicketLabel,
  openCalendar,
  buttonBg,
  hasPassengersModal,
  origin,
  destination,
  sourceUrl,
  forceHttps,
  useAvailability = false,
}) => {
  const compactUsedRef = useRef(false);
  const wrapperRef = useRef(null);
  const pendingContinueRef = useRef(false);
  const monthNavigationUsedRef = useRef(false);
  const tabChangesCountRef = useRef(0);
  const resetRangeOnNextClickRef = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const [modalTab, setModalTab] = useState('oneWay');
  // Internal Date objects for the inline calendar
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const toDateObj = str => toDate(str, dateFormat);
  const toStr = date => toDateString(date, dateFormat);

  // Close modal when clicking outside the wrapper (desktop only)
  useEffect(() => {
    if (!isOpen || getProductType() === 'web-mobile') return;

    const handleOutsideClick = e => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        closeModal();
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Sync calendar selection when external dates change while modal is open.
  // With no departure: no default selection (today is only highlighted in One-way).
  useEffect(() => {
    if (!isOpen) return;
    if (departureDate === openTicketText) {
      setStartDate(null);
    } else if (departureDate) {
      setStartDate(atDayStart(toDateObj(departureDate)));
    } else {
      setStartDate(null);
    }
    setEndDate(
      returnDate && returnDate !== openTicketText ? atDayStart(toDateObj(returnDate)) : null,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departureDate, returnDate]);

  useEffect(() => {
    if (!openCalendar) return;
    openModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openCalendar]);

  // Deferred onContinue: fires once departureDate propagates after handleContinue sets it
  useEffect(() => {
    if (!pendingContinueRef.current || !departureDate) return;
    pendingContinueRef.current = false;
    if (onContinue) onContinue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departureDate]);

  // ─── Tab state ─────────────────────────────────────────────────────────────

  const deriveTab = () => {
    if (tripType === 'openTicket') return 'openTicket';
    // Open departure uses the Round tab + single-day picker for the concrete leg (return by default).
    if (departureDate === openTicketText) return 'round';
    if (tripType === 'round' || returnDate) return 'round';
    return 'oneWay';
  };

  // Currently displayed month (controlled navigation)
  const [currentMonth, setCurrentMonth] = useState(null);
  const { availabilityWindow, resetWindows } = useAvailabilityWindow(currentMonth);

  // Single-select only when return is open and departure is concrete (pick outbound).
  // Same on One-way and Round tabs now that both always use the range calendar until this slot mode.
  // Open departure uses the normal range picker so the user sets both legs from scratch.
  const departureIsOpen = departureDate === openTicketText;
  const returnIsOpen = returnDate === openTicketText;
  const roundWithOpenSlot =
    (modalTab === 'oneWay' || modalTab === 'round') && returnIsOpen && !departureIsOpen;

  // Once Round has a departure, show and fetch return availability even if
  // the return date was prefilled by the system.
  const activeLegIsReturn =
    modalTab === 'round' && startDate !== null && !roundWithOpenSlot;

  const {
    departure: departureAvailability,
    return: returnAvailability,
    fetching: availabilityFetching,
  } = useCalendarAvailability({
    origin: origin?.slug,
    destination: destination?.slug,
    from: availabilityWindow?.from,
    to: availabilityWindow?.to,
    leg: activeLegIsReturn ? 'return' : 'departure',
    enabled: isOpen && useAvailability,
    options: { sourceUrl, forceHttps },
  });

  const windowHasData = (availability, dateWindow) => {
    if (!dateWindow) return false;
    return Object.keys(availability).some(k => k >= dateWindow.from && k <= dateWindow.to);
  };

  // The availability for the leg currently being fetched. Drives the pulse (so
  // the first call of each leg shows it for its own window) and every downstream
  // derivation (blocked days, price-level colouring, legend).
  const activeAvailability = activeLegIsReturn ? returnAvailability : departureAvailability;
  const hasDataForCurrentWindow = windowHasData(activeAvailability, availabilityWindow);

  const showAvailabilitySpinnerRaw = availabilityFetching && !hasDataForCurrentWindow;
  const showAvailabilitySpinner = useDebounce(showAvailabilitySpinnerRaw, 400);

  // ─── Calendar bounds & suggested return (toDateObj / toStr / atDayStart are above hooks)

  const today = dayjs()
    .startOf('day')
    .toDate();
  const maxDate = dayjs()
    .add(maxDaysSearch, 'day')
    .toDate();

  /** Suggested return date: departure + 3 days, capped at maxDate. */
  const suggestedReturnFrom = start => {
    let end = dayjs(start).add(3, 'day').startOf('day').toDate();
    if (dayjs(end).isAfter(dayjs(maxDate).startOf('day'))) {
      end = dayjs(maxDate).startOf('day').toDate();
    }
    return end;
  };

  const suggestedReturnDate = suggestedReturnFrom(today);
  // ─── Display value for the trigger ────────────────────────────────────────

  const showCompact = !departureDate && !compactUsedRef.current;

  // ─── Open / close modal ────────────────────────────────────────────────────

  const openModal = forceTab => {
    if (onFocus) onFocus();
    const tab = forceTab || deriveTab();
    let start = null;
    let end = null;

    if (departureDate === openTicketText) {
      // Fresh range: clear any concrete return so the user picks outbound then return.
      if (returnDate && returnDate !== openTicketText) {
        onReturnChange('');
      }
    } else if (departureDate) {
      start = toDateObj(departureDate);
      if (returnDate && returnDate !== openTicketText) {
        end = toDateObj(returnDate);
      }
    } else if (returnDate && returnDate !== openTicketText) {
      end = toDateObj(returnDate);
    }

    setModalTab(tab);
    setStartDate(atDayStart(start));
    setEndDate(atDayStart(end));
    setCurrentMonth(atDayStart(start) || today);
    resetRangeOnNextClickRef.current = Boolean(start || end) && !(forceTab === 'round' && start);
    monthNavigationUsedRef.current = false;
    tabChangesCountRef.current = 0;
    mixpanelTracker.trackEvent('Calendar Opened', {
      calendar_variant: 'new',
      has_availability: Object.keys(departureAvailability).length > 0,
    });
    setIsOpen(true);
    // Hides the chatbot in CDS. TODO: Find a way to show the modal above all the content
    document.querySelector('.bot-cta')?.style.setProperty('display', 'none');
  };

  const closeModal = () => {
    setIsOpen(false);
    setCurrentMonth(null);
    resetWindows();
    // Hides the chatbot in CDS. TODO: Find a way to show the modal above all the content
    document.querySelector('.bot-cta')?.style.setProperty('display', 'block');
  };

  const handleInputClick = fromReturn => {
    // Departure field: keep deriveTab() (one-way vs round vs open from current trip).
    // Return field: always open on the Round tab so the calendar is range mode for return.
    openModal(fromReturn ? 'round' : null);
  };

  // ─── Compact date quick-selects ────────────────────────────────────────────

  const handleQuickDate = daysOffset => {
    mixpanelTracker.trackEvent(CALENDAR_EVENT, {
      type: daysOffset === 0 ? 'today' : 'tomorrow',
      calendar_variant: 'new',
    });
    const date = dayjs()
      .add(daysOffset, 'day')
      .toDate();
    compactUsedRef.current = true;
    if (returnDate) onReturnChange('');
    onTripTypeChange('oneWay');
    onDepartureChange(toStr(date));
  };

  const handleMundialClick = e => {
    e?.stopPropagation?.();
    mixpanelTracker.trackEvent(CALENDAR_EVENT, { type: 'mundial', calendar_variant: 'new' });
    // Keep compact row visible (like calendar "Select") until a date is committed — avoids empty inputs.
    openModal('oneWay');
    setCurrentMonth(new Date(2026, 5, 1)); // June 2026
  };

  const handleCompactDateClick = e => {
    // Desktop: Modal renders inside this wrapper; clicks (e.g. Continue) bubble here and
    // would otherwise call openModal() right after closeModal() in the same tick.
    if (e?.target?.closest?.('#modalOverlay')) {
      return;
    }
    // Departure/return rows use their own onClick → openModal(forceTab). If we open again
    // here, openModal() uses deriveTab() and overwrites e.g. Return → Round back to One-way.
    if (
      e?.target?.closest?.('.departureDate-wrapper') ||
      e?.target?.closest?.('.returnDate-wrapper')
    ) {
      return;
    }
    mixpanelTracker.trackEvent(CALENDAR_EVENT, { type: 'choose', calendar_variant: 'new' });
    openModal();
  };

  // ─── Open ticket handlers (defined before handleTabChange to avoid TDZ) ────

  const handleOpenDepartureTicket = () => {
    mixpanelTracker.trackEvent('Open Ticket Selected', { type: 'departure' });
    compactUsedRef.current = true;
    if (returnDate !== openTicketText) {
      onReturnChange('');
    }
    onTripTypeChange('openTicket');
    onDepartureChange(openTicketText);
    if (onDepartureOpenTicket) onDepartureOpenTicket();
  };

  const handleOpenReturnTicket = () => {
    mixpanelTracker.trackEvent('Open Ticket Selected', { type: 'return' });
    compactUsedRef.current = true;
    onTripTypeChange('round');
    if (startDate) onDepartureChange(toStr(startDate));
    onReturnChange(openTicketText);
    if (onReturnOpenTicket) onReturnOpenTicket();
  };

  const handleToggleOpenReturn = () => {
    mixpanelTracker.trackEvent('Open Return Ticket Toggled', {
      enabled: !returnIsOpen,
      calendar_variant: 'new',
    });
    if (returnIsOpen) {
      const outbound =
        atDayStart(startDate || (departureDate && departureDate !== openTicketText
          ? toDateObj(departureDate)
          : null));

      onReturnChange('');
      setEndDate(null);
      setModalTab('oneWay');
      onTripTypeChange('oneWay');
      if (outbound) {
        setStartDate(outbound);
        onDepartureChange(toStr(outbound));
      }
    } else {
      handleOpenReturnTicket();
    }
  };

  // ─── Tab change inside modal ───────────────────────────────────────────────

  const handleTabChange = tab => {
    tabChangesCountRef.current += 1;
    mixpanelTracker.trackEvent('Search Calendar Tab Clicked', {
      section: TAB_TO_SECTION[tab],
      calendar_variant: 'new',
    });
    if (tab === 'openTicket') {
      setModalTab(tab);
      handleOpenDepartureTicket();
      return;
    }
    setModalTab(tab);

    if (tab === 'oneWay') {
      onTripTypeChange('oneWay');
      if (departureDate === openTicketText) {
        compactUsedRef.current = true;
        onDepartureChange(toStr(today));
        setCurrentMonth(today);
        setStartDate(atDayStart(today));
        setEndDate(null);
        if (returnDate) onReturnChange('');
        return;
      }

      const outbound =
        departureDate && departureDate !== openTicketText
          ? atDayStart(startDate || toDateObj(departureDate))
          : null;

      if (outbound && returnDate && returnDate !== openTicketText) {
        compactUsedRef.current = true;
        onDepartureChange(toStr(outbound));
        onReturnChange('');
        setStartDate(outbound);
        setEndDate(null);
        setCurrentMonth(outbound);
        return;
      }

      if (outbound) {
        if (returnDate) onReturnChange('');
        setStartDate(outbound);
        setEndDate(null);
        setCurrentMonth(outbound);
      } else {
        setStartDate(null);
        setEndDate(null);
      }
      return;
    }

    if (tab === 'round') {
      onTripTypeChange('round');
      const hasConcreteDeparture = Boolean(departureDate) && departureDate !== openTicketText;
      const hasConcreteReturn = Boolean(returnDate) && returnDate !== openTicketText;

      if (hasConcreteDeparture && hasConcreteReturn) {
        const s = atDayStart(toDateObj(departureDate));
        const e = atDayStart(toDateObj(returnDate));
        if (s && e) {
          setStartDate(s);
          setEndDate(e);
          setCurrentMonth(s);
        }
        return;
      }

      const start =
        atDayStart(startDate || (hasConcreteDeparture ? toDateObj(departureDate) : null)) || today;
      const end = suggestedReturnFrom(start);

      compactUsedRef.current = true;
      onDepartureChange(toStr(start));
      onReturnChange(toStr(end));
      setStartDate(start);
      setEndDate(end);
      setCurrentMonth(start);
      return;
    }
  };

  // ─── Calendar date selection handlers ─────────────────────────────────────

  const isDesktop = getProductType() !== 'web-mobile';

  const handleRoundOpenSlotChange = date => {
    if (!date) return;
    const hadReturnOpen = returnDate === openTicketText;
    const dateKey = dayjs(date).format('YYYY-MM-DD');
    mixpanelTracker.trackEvent(CALENDAR_EVENT, {
      type: 'personalized',
      tab: 'round',
      calendar_variant: 'new',
      price_level: departureAvailability[dateKey]?.priceLevel ?? null,
    });
    compactUsedRef.current = true;
    onTripTypeChange('round');

    // Return was "open ticket": clear it and go back to range mode (first day = start of range).
    if (hadReturnOpen) {
      onReturnChange('');
      onDepartureChange(toStr(date));
      setStartDate(date);
      setEndDate(null);
      return;
    }

    onDepartureChange(toStr(date));
    if (isDesktop) {
      closeModal();
    }
  };

  const handleSaveRangeDates = (start, end) => {
    compactUsedRef.current = true;
    onTripTypeChange('round');
    onDepartureChange(toStr(start));
    onReturnChange(toStr(end));
  };

  // react-day-picker range: onSelect receives { from, to } | undefined.
  // First click usually has `to` unset (outbound only). A second click on the same day
  // sends `from` and `to` equal (addToRange with min 0) — that must become a same-day
  // round trip even on the One-way tab, so we always keep `to` when the picker sends it.
  const handleRangeChange = (range, selectedDate) => {
    const clickedDate = atDayStart(selectedDate || range?.to || null);
    const resetToClickedDate = date => {
      resetRangeOnNextClickRef.current = false;
      setStartDate(date);
      setEndDate(null);
      setModalTab('oneWay');
      mixpanelTracker.trackEvent(CALENDAR_EVENT, {
        type: 'personalized',
        tab: 'oneWay',
        calendar_variant: 'new',
      });
      compactUsedRef.current = true;
      onTripTypeChange('oneWay');
      if (returnDate) onReturnChange('');
      onDepartureChange(toStr(date));
    };

    if (!range) {
      if (
        shouldResetRangeSelection({
          clickedDate,
          shouldResetSelection: resetRangeOnNextClickRef.current,
          startDate,
          endDate,
        })
      ) {
        resetToClickedDate(clickedDate);
        return;
      }

      // With resetOnSelect, a third click on a same-day range emits undefined (library
      // "clear"). We keep the current selection — users clear dates via the clean control.
      if (startDate != null || endDate != null) {
        return;
      }
      setStartDate(null);
      setEndDate(null);
      return;
    }
    const start = atDayStart(range?.from || null);
    const rawEnd = atDayStart(range?.to || null);
    const end = rawEnd && start ? rawEnd : null;

    if (
      shouldResetRangeSelection({
        clickedDate,
        shouldResetSelection: resetRangeOnNextClickRef.current,
        startDate,
        endDate,
      })
    ) {
      resetToClickedDate(clickedDate);
      return;
    }

    resetRangeOnNextClickRef.current = false;
    setStartDate(start);
    setEndDate(end);
    if (start && end) {
      setModalTab('round');
      const startKey = dayjs(start).format('YYYY-MM-DD');
      const endKey = dayjs(end).format('YYYY-MM-DD');
      mixpanelTracker.trackEvent(CALENDAR_EVENT, {
        type: 'personalized',
        tab: 'round',
        calendar_variant: 'new',
        start: start.toISOString(),
        end: end.toISOString(),
        difference: differenceInDays(end, start),
        departure_price_level: departureAvailability[startKey]?.priceLevel ?? null,
        return_price_level: returnAvailability[endKey]?.priceLevel ?? null,
      });
      handleSaveRangeDates(start, end);
    } else if (start && !end) {
      // Outbound only (incomplete range) → One-way tab until the user picks the return day (then Round).
      setModalTab('oneWay');
      const startKey = dayjs(start).format('YYYY-MM-DD');
      mixpanelTracker.trackEvent(CALENDAR_EVENT, {
        type: 'personalized',
        tab: 'oneWay',
        calendar_variant: 'new',
        price_level: departureAvailability[startKey]?.priceLevel ?? null,
      });
      compactUsedRef.current = true;
      onTripTypeChange('oneWay');
      if (returnDate) onReturnChange('');
      onDepartureChange(toStr(start));
    }
  };

  const handleTodayClick = () => {
    mixpanelTracker.trackEvent('Calendar Today Clicked', { calendar_variant: 'new' });
    compactUsedRef.current = true;
    onTripTypeChange('oneWay');
    if (returnDate) onReturnChange('');
    onDepartureChange(toStr(today));
    setStartDate(today);
    setEndDate(null);
    setModalTab('oneWay');
  };

  const handleContinue = () => {
    const eventProps = {
      tab: TAB_TO_SECTION[modalTab],
      is_open_departure: departureDate === openTicketText,
      is_open_return: returnDate === openTicketText,
      used_month_navigation: monthNavigationUsedRef.current,
      tab_changes_count: tabChangesCountRef.current,
      calendar_variant: 'new',
    };
    mixpanelTracker.trackEvent(CALENDAR_CONTINUE_EVENT, eventProps);

    closeModal();

    // One-way: no outbound date picked → default to today on Continue.
    if (modalTab === 'oneWay' && !departureDate) {
      if (startDate) {
        pendingContinueRef.current = true;
        onDepartureChange(toStr(startDate));
        return;
      }
      compactUsedRef.current = true;
      onTripTypeChange('oneWay');
      if (returnDate) onReturnChange('');
      onDepartureChange(toStr(today));
      setStartDate(today);
      setEndDate(null);
      pendingContinueRef.current = true;
      return;
    }

    // Round trip: nothing saved yet → defaults or partial range from calendar.
    if (!departureDate && modalTab === 'round' && !returnDate) {
      compactUsedRef.current = true;
      onTripTypeChange('round');
      if (startDate && endDate) {
        handleSaveRangeDates(startDate, endDate);
      } else if (startDate) {
        handleSaveRangeDates(startDate, suggestedReturnFrom(startDate));
      } else {
        handleSaveRangeDates(today, suggestedReturnDate);
      }
      pendingContinueRef.current = true;
      return;
    }

    if (onContinue) onContinue();
  };

  // ─── Clean buttons ─────────────────────────────────────────────────────────

  const handleCleanDeparture = e => {
    e.stopPropagation();
    compactUsedRef.current = false;
    onDepartureChange('');
    if (returnDate) onReturnChange('');
    setStartDate(null);
    setEndDate(null);
    setModalTab('oneWay');
    onTripTypeChange('oneWay');
    closeModal();
  };

  // ─── Continue footer date display ─────────────────────────────────────────

  const fmtDate = date => dayjs(date).format('D MMM');

  // True while the user has clicked the first day of a new range but not the second yet
  const isSelectingRange = startDate !== null && endDate === null;

  const footerDeparture = (() => {
    if (modalTab === 'openTicket') return openTicketText;
    if (departureDate === openTicketText) return openTicketText;
    if (startDate) return fmtDate(startDate);
    if (departureDate) return fmtDate(toDateObj(departureDate)) || departureDate;
    return null;
  })();

  const footerReturn = (() => {
    if (returnDate === openTicketText) return openTicketText;
    if (endDate) return fmtDate(endDate);
    // Hide the previous return date while the user is picking a new range start
    if (isSelectingRange) return null;
    if (returnDate) return fmtDate(toDateObj(returnDate)) || returnDate;
    return null;
  })();

  const shouldShowReturnNotSelected =
    returnDate !== openTicketText &&
    !endDate &&
    (isSelectingRange || !returnDate);

  const shouldShowDepartureNotSelected =
    departureDate !== openTicketText && !startDate && !departureDate;

  const showMobileFooterDates =
    modalTab === 'openTicket' ||
    footerDeparture ||
    shouldShowDepartureNotSelected ||
    footerReturn ||
    shouldShowReturnNotSelected;

  // ─── Badge text for the custom DayButton ──────────────────────────────────
  const dayBadgeText = roundWithOpenSlot && startDate ? departureBadge : null;

  // Today keeps accent + icon unless today is the range start or end.
  // Still applies with a full range or when today is inside the range.
  const startTouchesToday = startDate && dayjs(startDate).isSame(dayjs(today), 'day');
  const endTouchesToday = endDate && dayjs(endDate).isSame(dayjs(today), 'day');
  const showTodayAccentHint =
    modalTab !== 'openTicket' && !startTouchesToday && !endTouchesToday;

  const priceLevelDates = useMemo(
    () => groupDatesByPriceLevel(activeAvailability),
    [activeAvailability],
  );

  // The legend must reflect only the current availability window, not the union
  // of every window visited so far (the hook accumulates days across windows).
  const windowedAvailability = useMemo(() => {
    if (!availabilityWindow) return {};
    const { from, to } = availabilityWindow;
    return Object.fromEntries(
      Object.entries(activeAvailability).filter(
        ([dateKey]) => dateKey >= from && dateKey <= to,
      ),
    );
  }, [activeAvailability, availabilityWindow]);

  // While a newly entered window is still loading, keep showing the previously
  // loaded window's legend (instead of flickering to empty) until the new
  // window's data arrives and replaces it.
  const lastShownLegendRef = useRef({});
  const hasWindowData = Object.keys(windowedAvailability).length > 0;
  if (hasWindowData) {
    lastShownLegendRef.current = windowedAvailability;
  }
  const legendAvailability = hasWindowData
    ? windowedAvailability
    : lastShownLegendRef.current;

  // ─── Shared DayPicker props ────────────────────────────────────────────────

  const MonthCaptionWithFetching = useCallback(
    (props) => <MonthCaption {...props} availabilityFetching={showAvailabilitySpinner} />,
    [showAvailabilitySpinner]
  );

  const sharedDayPickerProps = {
    weekStartsOn: 1,
    disabled: [
      { before: today },
      { after: maxDate },
      makeUnavailableMatcher(activeAvailability),
    ],
    modifiers: {
      priceCheap: priceLevelDates.cheap,
      priceNormal: priceLevelDates.normal,
      priceExpensive: priceLevelDates.expensive,
    },
    modifiersClassNames: {
      priceCheap: 'udp-day-cell--cheap',
      priceNormal: 'udp-day-cell--normal',
      priceExpensive: 'udp-day-cell--expensive',
    },
    startMonth: today,
    endMonth: maxDate,
    month: currentMonth || today,
    onMonthChange: month => {
      const prevMonth = currentMonth || today;
      monthNavigationUsedRef.current = true;
      mixpanelTracker.trackEvent('Calendar Month Changed', {
        month: dayjs(month).month() + 1,
        action: dayjs(month).isAfter(dayjs(prevMonth), 'month') ? 'next' : 'prev',
        month_delta: dayjs(month).diff(dayjs().startOf('month'), 'month'),
        tab: TAB_TO_SECTION[modalTab],
        calendar_variant: 'new',
      });
      setCurrentMonth(month);
    },
    formatters: {
      formatCaption: date => dayjs(date).format('MMMM YYYY'),
      formatWeekdayName: date => dayjs(date).format('ddd'),
    },
    components: {
      Nav: HiddenNav,
      MonthCaption: MonthCaptionWithFetching,
    },
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div
      ref={wrapperRef}
      className={`form-field-date-wrap udp-wrapper${error ? ' udp-error' : ''} udp-trigger-row${
        showCompact ? ' udp-trigger-row--compact-controls' : ''
      }${isOpen ? ' udp-trigger-row--focused' : ''}`}
      onClick={handleCompactDateClick}
    >
      <DateTriggerRow
        error={error}
        showCompact={showCompact}
        locale={locale}
        handleCompactDateClick={handleCompactDateClick}
        handleQuickDate={handleQuickDate}
        handleMundialClick={handleMundialClick}
        showMundialButton={showMundialButton}
        showTomorrowWithMundial={showTomorrowWithMundial}
        departureDate={departureDate}
        returnDate={returnDate}
        openTicketText={openTicketText}
        openTicketLabel={openTicketLabel}
        returnPlaceholder={returnPlaceholder}
        handleInputClick={handleInputClick}
        handleCleanDeparture={handleCleanDeparture}
      />

      {/* ── Modal with tab buttons + calendar ────────────────────────────── */}
      {isOpen && (
        <Modal
          key="udp-modal"
          onClose={closeModal}
          title={<Text id="title.select_trip_type" />}
          contentClassName="udp-modal-content"
          modalClassName="udp-modal-unified"
          isFitContent
          showCloseButton
          footer={
            <div className="udp-continue-footer">
              {useAvailability && modalTab !== 'openTicket' && (
                <PriceLevelLegend availability={legendAvailability} />
              )}
              <div className="udp-continue-footer__cta-row">
                {showMobileFooterDates && (
                  <div className="udp-continue-dates udp-continue-dates--mobile-only">
                    <p className="udp-continue-row">
                      <span className="udp-continue-label">
                        <Text id="label.departure_date" />
                      </span>{' '}
                      <span className="udp-continue-value">
                        {shouldShowDepartureNotSelected ? (
                          <span className="udp-continue-not-selected">
                            <Text id="label.departure_not_selected" />
                          </span>
                        ) : (
                          footerDeparture
                        )}
                      </span>
                    </p>
                    <p className="udp-continue-row">
                      <span className="udp-continue-label">
                        <Text id="label.return_date" />
                      </span>{' '}
                      <span className="udp-continue-value">
                        {shouldShowReturnNotSelected ? (
                          <span className="udp-continue-not-selected">
                            <Text id="label.return_not_selected" />
                          </span>
                        ) : (
                          footerReturn || returnPlaceholder
                        )}
                      </span>
                    </p>
                  </div>
                )}
                <button
                  type="button"
                  className="udp-continue-btn"
                  onClick={handleContinue}
                  style={{ backgroundColor: buttonBg }}
                >
                  <Text id={hasPassengersModal ? 'button.continue' : 'button.search'} />
                </button>
              </div>
            </div>
          }
        >
          {/* Sentinel: when scrolled out of view the tabs are sticky */}
          {/* Tab selector */}
          <div className="udp-tabs">
            <button
              type="button"
              className={`udp-tab${modalTab === 'oneWay' ? ' active' : ''}`}
              onClick={() => handleTabChange('oneWay')}
            >
              <Text id="label.unified_one_way" />
            </button>

            <button
              type="button"
              className={`udp-tab${modalTab === 'round' ? ' active' : ''}`}
              onClick={() => handleTabChange('round')}
            >
              <Text id="label.unified_round" />
            </button>

            {showOpenTicket && (
              <button
                type="button"
                className={`udp-tab${modalTab === 'openTicket' ? ' active' : ''}`}
                onClick={() => handleTabChange('openTicket')}
              >
                <Text id="label.unified_open" />
              </button>
            )}
          </div>
          {modalTab !== 'openTicket' && (
            <CalendarSection
              sharedDayPickerProps={sharedDayPickerProps}
              today={today}
              roundWithOpenSlot={roundWithOpenSlot}
              startDate={startDate}
              endDate={endDate}
              dayBadgeText={dayBadgeText}
              showTodayAccentHint={showTodayAccentHint}
              departureAvailability={departureAvailability}
              returnAvailability={returnAvailability}
              onDayWithPriceHover={(dateKey, dayDataArg) => {
                mixpanelTracker.trackEvent('Calendar Price Day Hovered', {
                  date: dateKey,
                  price_level: dayDataArg.priceLevel,
                  min_price: dayDataArg.minPrice,
                  calendar_variant: 'new',
                  leg: activeLegIsReturn ? 'return' : 'departure',
                });
              }}
              handleRoundOpenSlotChange={handleRoundOpenSlotChange}
              handleRangeChange={handleRangeChange}
              handleTodayClick={handleTodayClick}
              onCloseClick={() => {
                mixpanelTracker.trackEvent('Calendar Close Clicked', {
                  calendar_variant: 'new',
                });
                closeModal();
              }}
              showOpenTicket={showOpenTicket}
              departureIsOpen={departureIsOpen}
              returnIsOpen={returnIsOpen}
              handleToggleOpenReturn={handleToggleOpenReturn}
              flatVariant={flatVariant}
            />
          )}

          {modalTab === 'openTicket' && (
            <OpenTicketSection
              flatVariant={flatVariant}
              returnIsOpen={returnIsOpen}
              handleToggleOpenReturn={handleToggleOpenReturn}
            />
          )}
        </Modal>
      )}
      {error && (
        <div
          className="icon-error"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: selectIcon('error', false) }}
        />
      )}
    </div>
  );
};

export default withText(() => ({
  returnPlaceholder: <Text id="placeholder.return_option" />,
  departureBadge: <Text id="label.departure_badge" />,
  openTicketLabel: <Text id="label.open_ticket_short" />,
}))(UnifiedDatePicker);
